/**
 * DatasetDetailPage — review grid for a single ML dataset.
 *
 * Shows the extracted rows in a tabular grid, lets users:
 *   - Accept / reject / flag rows
 *   - Edit cell values inline (double-click)
 *   - View per-cell provenance (click the source icon)
 *   - Trigger a rebuild
 *   - Export as CSV
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Content,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  Tooltip,
  Select,
  SelectItem,
  TextInput,
  Modal,
} from '@carbon/react';
import {
  ArrowLeft,
  Renew,
  Download,
  CheckmarkFilled,
  ErrorFilled,
  Time,
  DocumentView,
  Settings,
  Idea,
  Launch,
} from '@carbon/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { datasetApi } from '@/lib/api/dataset.api';
import { knowledgeUnitApi } from '@/lib/api/knowledge-unit.api';
import KnowledgeUnitCard from '@/components/knowledge-base/KnowledgeUnitCard';
import { EditDatasetConfigModal } from '@/components/dataset/EditDatasetConfigModal';
import type { DatasetRow, DatasetColumn, CellProvenance } from '@snxt/snxt-onto-share';

// ── helpers ────────────────────────────────────────────────────────────────────

function confidenceBadge(v: number | null) {
  if (v === null) return null;
  const pct = Math.round(v * 100);
  const color = v >= 0.8 ? '#24a148' : v >= 0.5 ? '#f1c21b' : '#da1e28';
  return (
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>
      {pct}%
    </span>
  );
}

function ReviewTag({ status }: { status: string }) {
  const map: Record<string, 'green' | 'warm-gray' | 'red' | 'gray'> = {
    accepted: 'green', edited: 'warm-gray', rejected: 'red', pending: 'gray',
  };
  return <Tag type={map[status] ?? 'gray'} size="sm">{status}</Tag>;
}

// ── KU detail flyout ───────────────────────────────────────────────────────────

interface KuFlyoutProps {
  kuId: string | null;
  onClose: () => void;
}

function KuFlyout({ kuId, onClose }: KuFlyoutProps) {
  const { data: kuDetail, isLoading, isError } = useQuery({
    queryKey: ['ku-detail', kuId],
    queryFn: () => knowledgeUnitApi.getDetail(kuId!),
    enabled: !!kuId,
    staleTime: 60_000,
  });

  return (
    <Modal
      open={!!kuId}
      modalHeading={kuDetail ? (kuDetail.unit.label ?? 'Knowledge Unit') : 'Knowledge Unit'}
      passiveModal
      onRequestClose={onClose}
      size="lg"
    >
      {isLoading && <InlineLoading description="Loading knowledge unit…" style={{ margin: '1rem' }} />}
      {isError && <p style={{ color: 'var(--cds-support-error)', margin: '1rem' }}>Failed to load knowledge unit.</p>}
      {kuDetail && (
        <>
          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.75rem' }}>
            From: <strong>{kuDetail.context.knowledge_base.name}</strong>
            {' / '}
            <strong>{kuDetail.context.knowledge_object.filename}</strong>
          </p>
          <KnowledgeUnitCard
            unit={kuDetail.unit}
            knowledgeObjectId={kuDetail.context.knowledge_object.id}
            summary={kuDetail.summary ?? undefined}
            extractions={kuDetail.extractions}
            forceExpanded
          />
        </>
      )}
    </Modal>
  );
}

// ── Provenance side-panel ──────────────────────────────────────────────────────

interface ProvenancePanelProps {
  open: boolean;
  onClose: () => void;
  row: DatasetRow | null;
  schema: DatasetColumn[];
}

function ProvenancePanel({ open, onClose, row, schema }: ProvenancePanelProps) {
  const [openKuId, setOpenKuId] = useState<string | null>(null);

  if (!row) return null;

  // Provenance stored as Record<col_key, CellProvenance>.
  // Older rows built before source_unit_id tracking may have source_unit_id = null.
  const allProv = (row.provenance ?? {}) as Record<string, CellProvenance & { source?: string }>;

  // Check if any cell has a linkable KU — used to decide whether to show the rebuild hint
  const hasAnyKuLink = Object.values(allProv).some(
    (p) => p && typeof (p as CellProvenance).source_unit_id === 'string' && (p as CellProvenance).source_unit_id
  );

  return (
    <>
      <Modal
        open={open}
        modalHeading={`Evidence — ${row.sample_ref ?? 'row'}`}
        passiveModal
        onRequestClose={onClose}
        size="md"
      >
        <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '1rem' }}>
          Source traces per extracted cell.{hasAnyKuLink ? ' Click "Open KU" to inspect the original chunk.' : ' Rebuild the dataset to enable KU links.'}
        </p>
        {schema.map(col => {
          const prov = allProv[col.key];
          const val  = (row.values as Record<string, unknown>)[col.key];
          return (
            <div key={col.key} style={{ marginBottom: '1rem', borderBottom: '1px solid var(--cds-border-subtle)', paddingBottom: '0.75rem' }}>
              <p style={{ fontWeight: 600, fontSize: '0.875rem' }}>{col.label}</p>
              <p style={{ fontSize: '0.875rem' }}>Value: <strong>{val === null || val === undefined ? '—' : String(val)}</strong>{col.unit ? ` ${col.unit}` : ''}</p>
              {prov ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                    {prov.source === 'prose_extraction'
                      ? `From document text (LLM prose extraction, raw: ${prov.value_raw})`
                      : prov.source === 'key_claims'
                        ? (() => { const kp = prov as unknown as Record<string, unknown>; return `From structured key_claims extraction — "${kp['claim_property'] ?? prov.source_column ?? 'property'}" of "${kp['claim_subject'] ?? row?.sample_ref ?? 'subject'}" (raw: ${prov.value_raw})${kp['unit'] ? ` [${kp['unit']}]` : ''}`; })()
                        : `From table column "${prov.source_column}" (raw: ${prov.value_raw})${prov.unit ? ` [${prov.unit}]` : ''}`
                    }
                  </span>
                  {prov.source_unit_id ? (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Launch}
                      onClick={() => setOpenKuId(prov.source_unit_id)}
                      style={{ padding: '0 0.5rem', minHeight: 'unset', fontSize: '0.75rem' }}
                    >
                      Open KU
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>No provenance recorded.</p>
              )}
            </div>
          );
        })}
        {row.confidence_note && (
          <p style={{ fontSize: '0.75rem', fontStyle: 'italic', marginTop: '0.5rem' }}>
            Note: {row.confidence_note}
          </p>
        )}
      </Modal>

      <KuFlyout kuId={openKuId} onClose={() => setOpenKuId(null)} />
    </>
  );
}

// ── Cell inline editor ─────────────────────────────────────────────────────────

interface EditableCellProps {
  value: unknown;
  onSave: (v: string) => void;
}

function EditableCell({ value, onSave }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  function start() {
    setDraft(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  }

  function commit() {
    onSave(draft);
    setEditing(false);
  }

  if (editing) {
    return (
      <TextInput
        id="cell-edit"
        labelText=""
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        size="sm"
        autoFocus
      />
    );
  }

  return (
    <span
      onDoubleClick={start}
      style={{ cursor: 'text', display: 'block', minHeight: '1.5rem' }}
      title="Double-click to edit"
    >
      {value === null || value === undefined ? <span style={{ color: 'var(--cds-text-placeholder)' }}>—</span> : String(value)}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function DatasetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [reviewFilter,   setReviewFilter]   = useState('');
  const [provenanceRow,  setProvenanceRow]  = useState<DatasetRow | null>(null);
  const [provenanceOpen, setProvenanceOpen] = useState(false);
  const [editConfigOpen, setEditConfigOpen] = useState(false);
  const [editConfigError, setEditConfigError] = useState<string | null>(null);
  const [buildError,     setBuildError]     = useState<string | null>(null);
  const [reasoningOpen,  setReasoningOpen]  = useState(false);

  const { data: dataset, isLoading: dsLoading, error: dsError } = useQuery({
    queryKey: ['dataset', id],
    queryFn: () => datasetApi.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const ds = query.state.data;
      return ds?.build_status === 'running' ? 3000 : false;
    },
  });

  const { data: rowsData, isLoading: rowsLoading } = useQuery({
    queryKey: ['dataset-rows', id, reviewFilter],
    queryFn: () => datasetApi.listRows(id!, { reviewStatus: reviewFilter || undefined, limit: 500 }),
    enabled: !!id && !!dataset,
  });

  const buildMutation = useMutation({
    mutationFn: () => datasetApi.triggerBuild(id!),
    onSuccess: () => {
      setBuildError(null);
      qc.invalidateQueries({ queryKey: ['dataset', id] });
    },
    onError: (e: unknown) => {
      // 409 = already running; surface a friendly message instead of a console error
      const status = (e as { status?: number })?.status;
      if (status === 409) {
        setBuildError('A build is already running. Wait for it to finish before triggering another.');
      } else {
        setBuildError('Failed to start build. Please try again.');
      }
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: (dto: Parameters<typeof datasetApi.update>[1]) => datasetApi.update(id!, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dataset', id] });
      setEditConfigOpen(false);
      setEditConfigError(null);
    },
    onError: (e: Error) => {
      setEditConfigError(e.message ?? 'Save failed');
    },
  });

  const updateRowMutation = useMutation({
    mutationFn: (args: Parameters<typeof datasetApi.updateRow>[2] & { rowId: string }) => {
      const { rowId, ...dto } = args;
      return datasetApi.updateRow(id!, rowId, dto);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataset-rows', id] }),
    onError: (e: unknown) => {
      // 404 = row was cleared by a concurrent rebuild — just refresh the list
      const status = (e as { status?: number })?.status;
      if (status === 404) {
        qc.invalidateQueries({ queryKey: ['dataset-rows', id] });
      }
      // other errors: swallow silently (the row list will be stale but won't crash)
    },
  });

  const setStatus = useCallback((row: DatasetRow, status: 'accepted' | 'rejected' | 'pending') => {
    updateRowMutation.mutate({ rowId: row.id, review_status: status });
  }, [updateRowMutation]);

  const editCell = useCallback((row: DatasetRow, key: string, newVal: string) => {
    updateRowMutation.mutate({
      rowId: row.id,
      review_status: 'edited',
      values: { ...(row.values as Record<string, unknown>), [key]: newVal },
    });
  }, [updateRowMutation]);

  if (dsLoading) return (
    <Content className="app-content">
      <InlineLoading description="Loading dataset…" style={{ margin: '2rem' }} />
    </Content>
  );

  if (dsError || !dataset) return (
    <Content className="app-content">
      <InlineNotification kind="error" title="Dataset not found" style={{ margin: '2rem' }} />
    </Content>
  );

  const schema = dataset.schema ?? [];
  const rows   = rowsData?.rows ?? [];
  const stats  = rowsData;

  return (
    <Content className="app-content">
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          iconDescription="Back"
          hasIconOnly
        />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{dataset.name}</h2>
          {dataset.description && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
              {dataset.description}
            </p>
          )}
        </div>

        {/* Build status */}
        {dataset.build_status === 'running' && (
          <InlineLoading description="Building…" style={{ width: 'auto' }} />
        )}
        {dataset.build_status === 'completed' && (
          <Tag type="green"><CheckmarkFilled size={12} style={{ marginRight: 4 }} />Completed</Tag>
        )}
        {dataset.build_status === 'failed' && (
          <Tag type="red"><ErrorFilled size={12} style={{ marginRight: 4 }} />Failed</Tag>
        )}
        {dataset.build_status === 'pending' && (
          <Tag type="gray"><Time size={12} style={{ marginRight: 4 }} />Pending</Tag>
        )}

        {dataset.build_log && dataset.build_log.length > 0 && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Idea}
            onClick={() => setReasoningOpen(true)}
          >
            Show reasoning
          </Button>
        )}

        <Button
          kind="ghost"
          size="sm"
          renderIcon={Settings}
          onClick={() => { setEditConfigError(null); setEditConfigOpen(true); }}
          disabled={dataset.build_status === 'running'}
        >
          Edit configuration
        </Button>

        <Button
          kind="secondary"
          size="sm"
          renderIcon={Renew}
          onClick={() => { setBuildError(null); buildMutation.mutate(); }}
          disabled={dataset.build_status === 'running' || buildMutation.isPending}
        >
          {dataset.build_status === 'pending' ? 'Run build' : 'Rebuild'}
        </Button>

        <Button
          kind="ghost"
          size="sm"
          renderIcon={Download}
          as="a"
          href={datasetApi.exportUrl(id!)}
          download
        >
          Export CSV
        </Button>
      </div>

      {/* ── Stats strip ── */}
      {stats && (
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          <span>Total rows: <strong>{stats.total}</strong></span>
          <span style={{ color: '#24a148' }}>Accepted: <strong>{stats.accepted}</strong></span>
          <span style={{ color: '#da1e28' }}>Rejected: <strong>{stats.rejected}</strong></span>
          <span style={{ color: '#6f6f6f' }}>Pending: <strong>{stats.pending}</strong></span>
          {dataset.confidence_overall !== null && (
            <span>Overall confidence: {confidenceBadge(dataset.confidence_overall)}</span>
          )}
        </div>
      )}

      {/* ── Build error ── */}
      {dataset.build_status === 'failed' && dataset.build_error && (
        <InlineNotification
          kind="error"
          title="Build failed"
          subtitle={dataset.build_error}
          style={{ marginBottom: '1rem' }}
        />
      )}
      {buildError && (
        <InlineNotification
          kind="warning"
          title=""
          subtitle={buildError}
          lowContrast
          onCloseButtonClick={() => setBuildError(null)}
          style={{ marginBottom: '1rem' }}
        />
      )}

      {/* ── Filter ── */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <Select
          id="review-filter"
          labelText="Filter by review status"
          value={reviewFilter}
          onChange={e => setReviewFilter(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <SelectItem value="" text="All" />
          <SelectItem value="pending"  text="Pending" />
          <SelectItem value="accepted" text="Accepted" />
          <SelectItem value="rejected" text="Rejected" />
          <SelectItem value="edited"   text="Edited" />
        </Select>
      </div>

      {/* ── Grid ── */}
      {rowsLoading ? (
        <InlineLoading description="Loading rows…" />
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
          {dataset.build_status === 'pending'
            ? 'No rows yet — click "Run build" to start the agentic extraction.'
            : 'No rows match the current filter.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--cds-layer-accent)', borderBottom: '2px solid var(--cds-border-subtle)' }}>
                <th style={TH}>Sample</th>
                {schema.map(col => (
                  <th key={col.key} style={TH}>
                    {col.label}
                    {col.unit && <span style={{ color: 'var(--cds-text-secondary)', fontSize: '0.7rem' }}> ({col.unit})</span>}
                    {col.role === 'target' && <Tag type="blue" size="sm" style={{ marginLeft: 4 }}>target</Tag>}
                  </th>
                ))}
                <th style={TH}>Conf.</th>
                <th style={TH}>Status</th>
                <th style={TH}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: '1px solid var(--cds-border-subtle)',
                    background: row.review_status === 'rejected'
                      ? 'var(--cds-layer-selected-disabled, #e8e8e8)'
                      : 'transparent',
                    opacity: row.review_status === 'rejected' ? 0.6 : 1,
                  }}
                >
                  <td style={TD}>{row.sample_ref ?? '—'}</td>
                  {schema.map(col => (
                    <td key={col.key} style={TD}>
                      <EditableCell
                        value={(row.values as Record<string, unknown>)[col.key]}
                        onSave={v => editCell(row, col.key, v)}
                      />
                    </td>
                  ))}
                  <td style={TD}>{confidenceBadge(row.confidence)}</td>
                  <td style={TD}><ReviewTag status={row.review_status} /></td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    <Tooltip description="View evidence" align="bottom">
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={DocumentView}
                        hasIconOnly
                        iconDescription="View evidence"
                        onClick={() => { setProvenanceRow(row); setProvenanceOpen(true); }}
                      />
                    </Tooltip>
                    {row.review_status !== 'accepted' && (
                      <Tooltip description="Accept" align="bottom">
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={CheckmarkFilled}
                          hasIconOnly
                          iconDescription="Accept"
                          onClick={() => setStatus(row, 'accepted')}
                        />
                      </Tooltip>
                    )}
                    {row.review_status !== 'rejected' && (
                      <Tooltip description="Reject" align="bottom">
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={ErrorFilled}
                          hasIconOnly
                          iconDescription="Reject"
                          onClick={() => setStatus(row, 'rejected')}
                          style={{ color: 'var(--cds-support-error, #da1e28)' }}
                        />
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Provenance panel ── */}
      <ProvenancePanel
        open={provenanceOpen}
        onClose={() => setProvenanceOpen(false)}
        row={provenanceRow}
        schema={schema}
      />

      {/* ── Reasoning / build log modal ── */}
      <Modal
        open={reasoningOpen}
        modalHeading="Build reasoning"
        passiveModal
        onRequestClose={() => setReasoningOpen(false)}
        size="lg"
      >
        <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '1rem' }}>
          Per-document agent decisions from the last build. Shows how each paper was processed,
          which tables were used, and why rows were or were not extracted.
        </p>
        {(dataset.build_log ?? []).map((entry, idx) => {
          const statusColor: Record<string, string> = {
            ok: '#24a148',
            no_tables: '#f1c21b',
            join_failed: '#ff832b',
            skipped: '#6f6f6f',
            error: '#da1e28',
          };
          const color = statusColor[entry.status] ?? '#6f6f6f';

          // column_mapping: split into mapped vs unmapped
          const mappedCols = entry.column_mapping
            ? Object.entries(entry.column_mapping).filter(([, src]) => src != null)
            : [];
          const unmappedCols = entry.columns_unmapped ?? [];

          return (
            <div
              key={idx}
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                border: `1px solid var(--cds-border-subtle)`,
                borderLeft: `4px solid ${color}`,
                borderRadius: '4px',
                background: 'var(--cds-layer)',
              }}
            >
              {/* ── Header row ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {entry.ko_name ?? entry.ko_id}
                </span>
                <Tag type={entry.status === 'ok' ? 'green' : entry.status === 'error' ? 'red' : 'warm-gray'} size="sm">
                  {entry.status}
                </Tag>
                {entry.rows_extracted > 0 && (
                  <Tag type="blue" size="sm">{entry.rows_extracted} row{entry.rows_extracted !== 1 ? 's' : ''}</Tag>
                )}
                {entry.confidence !== null && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                    conf. {Math.round(entry.confidence * 100)}%
                  </span>
                )}
                {entry.join_key && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                    join on <code style={{ fontFamily: 'monospace', background: 'var(--cds-layer-02)', padding: '0 3px', borderRadius: '2px' }}>{entry.join_key}</code>
                  </span>
                )}
              </div>

              {/* ── ID format ── */}
              {entry.id_format && (
                <p style={{ fontSize: '0.75rem', margin: '0.25rem 0 0.5rem', color: 'var(--cds-text-secondary)' }}>
                  🔑 <strong>ID format:</strong> {entry.id_format}
                </p>
              )}

              {/* ── Plan notes ── */}
              {entry.plan_notes && (
                <div style={{
                  fontSize: '0.75rem',
                  margin: '0 0 0.5rem',
                  padding: '0.4rem 0.6rem',
                  background: 'var(--cds-layer-02)',
                  borderRadius: '4px',
                  color: 'var(--cds-text-primary)',
                  lineHeight: 1.5,
                }}>
                  <strong>📋 Plan:</strong>
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                    {entry.plan_notes.split(' | ').map((segment, si) => (
                      <li key={si} style={{ marginBottom: '0.15rem' }}>{segment.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Detected tables ── */}
              {entry.tables_detected && entry.tables_detected.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, margin: '0 0 0.25rem', color: 'var(--cds-text-secondary)' }}>
                    📊 Detected tables ({entry.tables_detected.length}):
                  </p>
                  {entry.tables_detected.map((t, ti) => (
                    <div key={ti} style={{
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                      marginBottom: '0.2rem',
                      background: 'var(--cds-layer-02)',
                      borderRadius: '3px',
                      color: 'var(--cds-text-secondary)',
                    }}>
                      <strong>T{ti + 1}{t.caption ? ` — ${t.caption}` : ''}</strong>
                      {' · '}{t.row_count} row{t.row_count !== 1 ? 's' : ''}
                      {t.orientation && ` · ${t.orientation}`}
                      {t.sample_column && (
                        <> · sample col: <code style={{ fontFamily: 'monospace' }}>{t.sample_column}</code></>
                      )}
                      {t.headers.length > 0 && (
                        <span style={{ marginLeft: '0.35rem', color: 'var(--cds-text-placeholder)' }}>
                          [{t.headers.join(' | ')}]
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Column mapping ── */}
              {mappedCols.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, margin: '0 0 0.25rem', color: 'var(--cds-text-secondary)' }}>
                    🔗 Column mapping ({mappedCols.length} matched{unmappedCols.length > 0 ? `, ${unmappedCols.length} missing` : ''}):
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {mappedCols.map(([key, src]) => (
                      <span key={key} style={{
                        fontSize: '0.7rem',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        background: 'var(--cds-tag-background-green, #defbe6)',
                        color: 'var(--cds-tag-color-green, #0e6027)',
                        fontFamily: 'monospace',
                      }}>
                        {key} ← {src}
                      </span>
                    ))}
                    {unmappedCols.map((key) => (
                      <span key={key} style={{
                        fontSize: '0.7rem',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        background: 'var(--cds-tag-background-red, #fff1f1)',
                        color: 'var(--cds-tag-color-red, #750e13)',
                        fontFamily: 'monospace',
                      }}>
                        {key} — not found
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Prose enrichment ── */}
              {entry.prose_enrichment && (
                <p style={{ fontSize: '0.75rem', margin: '0.25rem 0', color: 'var(--cds-text-secondary)' }}>
                  📝 <strong>Prose enrichment:</strong>{' '}
                  {entry.prose_enrichment.attempted
                    ? entry.prose_enrichment.filled.length > 0
                      ? `filled ${entry.prose_enrichment.filled.length} column(s) from text: ${entry.prose_enrichment.filled.join(', ')}`
                      : 'attempted but no additional values found in prose'
                    : 'not attempted (all values came from tables)'}
                </p>
              )}

              {/* ── Tables used count (fallback info) ── */}
              {entry.tables_used.length > 0 && !entry.tables_detected && (
                <p style={{ fontSize: '0.7rem', margin: '0.25rem 0', color: 'var(--cds-text-secondary)' }}>
                  Tables used: {entry.tables_used.length} KU{entry.tables_used.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          );
        })}
      </Modal>

      {/* ── Edit configuration modal ── */}
      <EditDatasetConfigModal
        open={editConfigOpen}
        dataset={dataset}
        onClose={() => setEditConfigOpen(false)}
        loading={updateConfigMutation.isPending}
        error={editConfigError}
        onSave={(dto, rebuild) => {
          updateConfigMutation.mutate(dto, {
            onSuccess: () => {
              if (rebuild) {
                datasetApi.triggerBuild(id!)
                  .then(() => qc.invalidateQueries({ queryKey: ['dataset', id] }))
                  .catch((e: unknown) => {
                    const status = (e as { status?: number })?.status;
                    if (status !== 409) {
                      setBuildError('Failed to start build after saving. Please trigger it manually.');
                    }
                  });
              }
            },
          });
        }}
      />
    </Content>
  );
}

// ── Table cell styles ──────────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  verticalAlign: 'middle',
};

// Made with Bob
